import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  REACT_APP_WX_COPILOT_URL,
  REACT_APP_WX_LAUNCH_DARKLY_CLIENT_SIDE_ID,
} from 'runtimeEnvVars';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { domainMeta } from 'utils/domainMeta';

import useWxJwtReady from './useWxJwtReady';

// Global state to track copilot lifecycle across hook instances.
//
// Each Cue mount (panel-root, trigger) is tracked by the actual DOM
// element it was rendered into, NOT just by mountId. The trigger lives
// inside GlobalNav, which dent remounts on every navigation — when that
// happens, React creates a new placeholder div with the same id, and the
// previously-rendered React root is left attached to the old detached
// node. Comparing by element identity lets us detect that swap and call
// the UMD's `render()` again (which internally cleans up the old root)
// so the trigger appears in the freshly-mounted host.
//
// IMPORTANT: Store on window to survive HMR reloads.
const getCopilotState = () => {
  if (typeof window === 'undefined') {
    return {
      rendered: new Map(),
      pollInterval: null,
      loadListener: null,
    };
  }
  if (!window.wxCopilotStateInternal) {
    window.wxCopilotStateInternal = {
      rendered: new Map(),
      pollInterval: null,
      loadListener: null,
    };
  } else if (
    !window.wxCopilotStateInternal.rendered ||
    typeof window.wxCopilotStateInternal.rendered.get !== 'function'
  ) {
    // Migrate older shapes (singleton flag, or Set-of-ids) to the per-id
    // element Map. We can't recover the old element reference, so seed
    // with null — the next render attempt will repopulate it.
    const legacy = window.wxCopilotStateInternal;
    const ids =
      legacy.rendered instanceof Set
        ? Array.from(legacy.rendered)
        : legacy.isRendered && legacy.mountId
          ? [legacy.mountId]
          : [];
    const map = new Map();
    ids.forEach(id => map.set(id, null));
    window.wxCopilotStateInternal.rendered = map;
  }
  return window.wxCopilotStateInternal;
};

export default function useWxCopilot({
  mountId = 'wx-copilot-root',
  renderProps = {},
}) {
  const whoami = useSelector(makeSelectWhoami());
  const isJwtReady = useWxJwtReady();

  // Store latest values in refs to avoid stale closures
  const whoamiRef = useRef(whoami);
  const renderPropsRef = useRef(renderProps);
  const mountIdRef = useRef(mountId);

  whoamiRef.current = whoami;
  renderPropsRef.current = renderProps;
  mountIdRef.current = mountId;

  // Stable boolean: false until whoami arrives, then true forever.
  // Using this in effect 2 deps instead of `whoami` directly prevents the
  // effect from cycling on every Redux dispatch — makeSelectWhoami() creates
  // a new selector per render, so `whoami` gets a new object reference on
  // each store update even when the data hasn't changed. That would
  // continuously kill and re-create localPollInterval, causing the trigger
  // instance's poll to be cleared right before the UMD loads.
  const isWhoamiLoaded = Boolean(whoami?.email);

  // Memoized render function
  const renderCopilot = useCallback(() => {
    const copilotState = getCopilotState();
    if (typeof window === 'undefined') return;

    const currentWhoami = whoamiRef.current;
    const currentMountId = mountIdRef.current;

    // Prefer an existing in-flow mount target (e.g. the GlobalNav-rendered
    // <div id="wx-cue-trigger" />) so the trigger lives where its host
    // placed it rather than floating at body level. Fall back to a
    // body-level div for the panel-root or other singletons that the host
    // doesn't render directly.
    let mountEl = document.getElementById(currentMountId);
    if (!mountEl) {
      mountEl = document.createElement('div');
      mountEl.id = currentMountId;
      document.body.appendChild(mountEl);
    }

    // Skip only when we've already rendered into THIS exact DOM node.
    // After a host remount the id is reused but the element is new, so a
    // pure id-based check would leave the trigger blank.
    if (copilotState.rendered.get(currentMountId) === mountEl) {
      return;
    }

    // Configure the component
    if (window.WxCopilotComponent?.setConfig) {
      try {
        window.WxCopilotComponent.setConfig({
          fountainAiBaseUrl: currentWhoami?.fountain_ai_base_url,
          wxServiceBaseUrl: currentWhoami?.account_wx_api_url,
          stage: domainMeta.environment,
          employerDomain: currentWhoami?.account_wx_domain,
          employerBasePath: '',
          launchDarklyClientSideId: REACT_APP_WX_LAUNCH_DARKLY_CLIENT_SIDE_ID,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('WxCopilotComponent.setConfig failed', e);
      }
    }

    // Render the component
    if (window.WxCopilotComponent?.render) {
      try {
        window.WxCopilotComponent.render(mountEl.id, renderPropsRef.current);
        copilotState.rendered.set(currentMountId, mountEl);
        // rAF ensures WxCopilotComponent:rendered fires after render() settles so Intercom boots with the correct isPanelOpen.
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent('WxCopilotComponent:rendered'));
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('WxCopilotComponent.render failed', e);
      }
    }
  }, []);

  // Effect 1: Load the script (runs once)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const src = REACT_APP_WX_COPILOT_URL;

    // Check if the script is already added to avoid duplicates
    const scripts = Array.from(document.getElementsByTagName('script'));
    const alreadyAdded = scripts.some(
      s =>
        s.id === 'wx-copilot-umd' ||
        s.getAttribute('data-wx-copilot-src') === src ||
        s.src === src,
    );

    if (!alreadyAdded) {
      const script = document.createElement('script');
      script.id = 'wx-copilot-umd';
      script.setAttribute('data-wx-copilot-src', src);
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }
    // No cleanup - script should persist
  }, []);

  // Effect 2: Render when ready (polls until conditions are met)
  useEffect(() => {
    const copilotState = getCopilotState();
    if (typeof window === 'undefined') return undefined;
    if (!whoamiRef.current) return undefined;
    if (!isJwtReady) return undefined;

    // If already loaded, only render if the mount element has changed.
    // Without this guard, every whoami reference change (Redux returns a new
    // object on each store update) would re-run this effect and call
    // renderCopilot repeatedly.
    if (window.WxCopilotComponent?.render) {
      const mountEl = document.getElementById(mountId);
      if (!mountEl || copilotState.rendered.get(mountId) !== mountEl) {
        renderCopilot();
      }
      return undefined;
    }

    // Use LOCAL closure variables — NOT the global copilotState — so that two
    // concurrent instances (panel-root + trigger) don't overwrite each other's
    // interval reference. Without this, the second instance's setInterval
    // overwrites copilotState.pollInterval; when the first instance's interval
    // fires and calls clearInterval(state.pollInterval) it clears the second
    // instance's interval instead of its own, leaking the first instance's
    // interval and preventing the second instance from ever rendering.
    let localPollInterval = null;

    const onLoaded = () => {
      renderCopilot();
      if (localPollInterval) {
        clearInterval(localPollInterval);
        localPollInterval = null;
      }
    };

    window.addEventListener('WxCopilotComponent:loaded', onLoaded);

    localPollInterval = setInterval(() => {
      if (window.WxCopilotComponent?.render) {
        clearInterval(localPollInterval);
        localPollInterval = null;
        renderCopilot();
      }
    }, 200);

    // Cleanup polling on unmount, but DON'T destroy copilot here
    return () => {
      if (localPollInterval) {
        clearInterval(localPollInterval);
        localPollInterval = null;
      }
      window.removeEventListener('WxCopilotComponent:loaded', onLoaded);
    };
  }, [isWhoamiLoaded, isJwtReady, renderCopilot, mountId]);

  // NOTE: We intentionally do NOT destroy the copilot on component unmount.
  // The panel-root mount is a body-level singleton that must survive any
  // host React re-render/remount, and trigger mounts are stable for the
  // lifetime of the placement that owns them. Destruction only happens on
  // full-page reload.
}
